import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUserContext } from '../context/UserContext';
import { useCampContext } from '../context/CampContext';
import { LoginModalProvider } from '../context/LoginModalContext';
import { useGetSharedSchedule, useSaveSharedSchedule } from '../api/schedules';
import { useAnalytics } from '../context/AnalyticsContext';
import Header from '../components/Header';
import Calendar from '../components/schedule/Calendar';
import './SharedScheduleInvite.css';
import { CalendarDays, Save } from 'lucide-react';
import RegisterForm from '../components/login/RegisterForm';
import LoginModal from '../components/login/LoginModal';
import { useNotifications } from '../context/NotificationContext';

const SharedScheduleInvite: React.FC = () => {
  const { isLoggedIn } = useUserContext();
  const { setSchedules, setSchedule, setSelectedScheduleIds } = useCampContext();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { showSuccess, showError } = useNotifications();
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const { scheduleUuid } = useParams<{ scheduleUuid: string }>();
  const shareId = queryParams.get("share_id");
  const { data, isLoading } = useGetSharedSchedule(
    scheduleUuid ?? "",
    shareId ?? "",
    { enabled: isLoggedIn && Boolean(scheduleUuid) && Boolean(shareId) }
  );
  const saveSharedScheduleMutation = useSaveSharedSchedule();
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView('/schedule/invite', 'Shared Schedule Invite');
  }, [trackPageView]);

  // Set the shared schedule in the context when loaded
  useEffect(() => {
    if (data) {
      setSchedules([data]);
      setSelectedScheduleIds([data.uuid]);
    }
  }, [data]); // Only depend on data, setter functions are stable

  const handleRegister = (email: string) => {
    showSuccess("You have successfully registered! You can now view the shared schedule and create your own.");
  };

  const handleSaveSchedule = () => {
    if (!scheduleUuid || !shareId) return;

    saveSharedScheduleMutation.mutate(
      { scheduleUuid, shareId },
      {
        onSuccess: (savedSchedule) => {
          showSuccess(`Calendar "${savedSchedule.name}" saved to your calendars!`);
          navigate('/schedule');
        },
        onError: (error) => {
          showError('Failed to save calendar', error);
        },
      }
    );
  };

  const getSubtitleText = () => {
    if (isLoading) {
      return 'Loading shared schedule...';
    }
    if (data?.name) {
      return `Someone has shared the "${data.name}" schedule with you.`;
    }
    return 'View this shared schedule.';
  };

  // Calculate the earliest event date for initial calendar view
  const initialDate = useMemo(() => {
    if (!data) return null;

    const dates: Date[] = [];

    // Add camp dates
    if (data.scheduled_camps) {
      data.scheduled_camps.forEach(scheduled => {
        dates.push(new Date(scheduled.camp_date.start_date));
      });
    }

    // Add custom event dates
    if (data.custom_events) {
      data.custom_events.forEach(event => {
        dates.push(new Date(event.start_date));
      });
    }

    // Return the earliest date, or null if no events
    if (dates.length === 0) return null;

    return new Date(Math.min(...dates.map(d => d.getTime())));
  }, [data]);

  if (!isLoggedIn) {
    return (
      <main className="shared-invite-main">
        <div className="shared-invite-container">
          <div className="shared-invite-card">
            <div className="shared-invite-icon">
              <CalendarDays size={64} strokeWidth={1.5} stroke="var(--primary-color)" />
            </div>

            <h1 className="shared-invite-title">Welcome to Aika!</h1>
            <p className="shared-invite-description">
              Someone you know has shared their camp schedule with you.
              Create a free account to view this personalized schedule and plan your child's activities.
            </p>
            <LoginModalProvider initialStep="register">
              <RegisterForm onRegister={handleRegister} onClose={() => {}} />
              <p className="login-link">
                Already have an account? <button
                  type="button"
                  className="login-link-text"
                  onClick={() => setShowLoginModal(true)}
                >
                  Sign in
                </button>
              </p>
            </LoginModalProvider>

            <div className="shared-invite-actions">
              <button
                className="btn btn--secondary btn--large"
                onClick={() => navigate('/')}
              >
                Learn More About Us
              </button>
            </div>

            <div className="shared-invite-info">
              <p className="shared-invite-info-text">
                <strong>What you'll discover:</strong>
              </p>
              <ul className="shared-invite-info-list">
                <li>Quick and easy sign-up - completely free</li>
                <li>View the shared schedule</li>
                <li>Create and manage your own schedules</li>
                <li>Save favorites and plan your child's camps</li>
              </ul>
            </div>
          </div>
        </div>
        {/* Render LoginModal when needed */}
        {showLoginModal && (
          <LoginModalProvider initialStep="email">
            <LoginModal onClose={() => setShowLoginModal(false)} onLogin={() => {}} onRegister={() => {}} />
          </LoginModalProvider>
        )}
      </main>
    );
  }

  return (
    <div className="shared-schedule-page">
      <Header />
      <main className="shared-schedule-main">
        <div className="shared-schedule-container">
          <div className="shared-schedule-header">
            <div className="shared-schedule-title-section">
              <h1 className="shared-schedule-title">
                <CalendarDays className="icon" />
                {data?.name || 'Shared Schedule'}
              </h1>
              <p className="shared-schedule-subtitle">
                {getSubtitleText()}
              </p>
            </div>
            {data && (
              <button
                className="btn btn--primary"
                onClick={handleSaveSchedule}
                disabled={saveSharedScheduleMutation.isPending}
              >
                <Save className="icon icon--sm" />
                {saveSharedScheduleMutation.isPending ? 'Saving...' : 'Save Calendar'}
              </button>
            )}
          </div>

          {isLoading && (
            <div className="shared-schedule-loading">
              <div className="loading-spinner"></div>
              <p>Loading shared schedule...</p>
            </div>
          )}

          {!isLoading && data && (
            <div className="shared-schedule-calendar">
              <Calendar initialDate={initialDate} />
            </div>
          )}

          {!isLoading && !data && (
            <div className="shared-schedule-empty">
              <div className="empty-state">
                <div className="empty-icon">
                  <CalendarDays size={64} strokeWidth={1.5} />
                </div>
                <h2>Schedule Not Found</h2>
                <p>This shared schedule is empty or the link may have expired.</p>
                <button
                  className="btn btn--primary"
                  onClick={() => navigate('/schedule')}
                >
                  Go to My Schedules
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SharedScheduleInvite;
